import api, { handleResponse, handleError } from './api';
import { Capacitor } from '@capacitor/core';

/**
 * Web Push subscription helpers. Wraps:
 *   1. Fetching the VAPID public key from the server
 *   2. Asking the browser for Notification permission
 *   3. Calling pushManager.subscribe() with that key
 *   4. POSTing the resulting subscription to /api/push/subscribe
 *
 * Idempotent — if a subscription already exists (subscription persists across
 * tabs/restarts), we re-POST it to refresh the user binding (handles a user
 * switching accounts on the same browser).
 */

// Two cooldowns:
//   - DISMISS_KEY: user actively said no (clicked Not now, or browser denied).
//     Long cooldown so we don't pester someone who's already declined.
//   - FAILURE_KEY: subscribe broke for a non-user reason (backend missing
//     VAPID config, pushManager threw, network blip). Short cooldown so the
//     user gets re-prompted next session and we silently recover when the
//     underlying problem is fixed.
const PUSH_DISMISS_KEY = 'pr:push_prompt_dismissed_at';
const PUSH_FAILURE_KEY = 'pr:push_prompt_last_failure_at';
const PUSH_DISMISS_DAYS = 14;
const PUSH_FAILURE_MINUTES = 60;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  // iPadOS 13+ identifies as Macintosh but has touch points — sniff both.
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua)
    || (ua.includes('Mac') && typeof document !== 'undefined' && 'ontouchend' in document);
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  if (window.navigator && window.navigator.standalone === true) return true; // iOS
  try { return window.matchMedia('(display-mode: standalone)').matches; } catch { return false; }
}

const pushService = {
  /** True inside the iOS/Android Capacitor shell. Web app reports false. */
  isNative() {
    try { return Capacitor.isNativePlatform(); } catch { return false; }
  },

  isSupported() {
    // Native shell always supports push (iOS APNs / Android FCM) regardless
    // of whether the WebView exposes PushManager.
    if (this.isNative()) return true;
    return typeof window !== 'undefined'
      && 'serviceWorker' in navigator
      && 'PushManager' in window
      && 'Notification' in window;
  },

  /**
   * Richer support status for the UI. Distinguishes:
   *   - 'supported'             — full push works right now
   *   - 'ios-install-required'  — iOS Safari, push only fires for installed PWAs
   *   - 'unsupported'           — actually no push (old browser / desktop Safari pre-16, etc.)
   * Without this, iOS Safari users see "not supported" which is wrong — they
   * can absolutely get push, they just have to Add to Home Screen first.
   */
  supportStatus() {
    if (typeof window === 'undefined') return 'unsupported';
    if (this.isNative()) return 'supported';
    if (this.isSupported()) return 'supported';
    if (isIOS() && !isStandalone()) return 'ios-install-required';
    return 'unsupported';
  },

  isIOS,
  isStandalone,

  permission() {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission; // 'default' | 'granted' | 'denied'
  },

  /**
   * The OS-level permission inside the native shell.
   *
   * permission() above reads the Web Notification API, which the iOS WebView
   * doesn't expose — so on native it always answered 'unsupported'. That made
   * the app blind to a permission the user had ALREADY granted: it would keep
   * re-showing the soft prompt after each cooldown, and never silently re-bind
   * a registration that had failed. Async, because the native check is.
   */
  async nativePermission() {
    if (!this.isNative()) return this.permission();
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const { receive } = await PushNotifications.checkPermissions();
      // Capacitor: 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied'
      if (receive === 'granted') return 'granted';
      if (receive === 'denied') return 'denied';
      return 'default';
    } catch {
      return 'default';
    }
  },

  /** True when we should NOT show the soft-prompt right now (user dismissed
   *  recently OR a recent failed attempt is still cooling down). */
  inCooldown() {
    try {
      const dismissAt = parseInt(localStorage.getItem(PUSH_DISMISS_KEY) || '0', 10);
      if (dismissAt && Date.now() - dismissAt < PUSH_DISMISS_DAYS * 24 * 60 * 60 * 1000) return true;
      const failAt = parseInt(localStorage.getItem(PUSH_FAILURE_KEY) || '0', 10);
      if (failAt && Date.now() - failAt < PUSH_FAILURE_MINUTES * 60 * 1000) return true;
      return false;
    } catch { return false; }
  },

  /** User actively said no — long cooldown. */
  setDismissCooldown() {
    try { localStorage.setItem(PUSH_DISMISS_KEY, String(Date.now())); } catch {}
  },

  /** Subscribe broke for a non-user reason — short cooldown so we recover next session. */
  setFailureCooldown() {
    try { localStorage.setItem(PUSH_FAILURE_KEY, String(Date.now())); } catch {}
  },

  /** Clear both cooldowns — call when a subscribe actually succeeds so a
   *  future "permission revoked then re-granted" cycle isn't blocked. */
  clearCooldowns() {
    try {
      localStorage.removeItem(PUSH_DISMISS_KEY);
      localStorage.removeItem(PUSH_FAILURE_KEY);
    } catch {}
  },

  async getVapidKey() {
    try {
      const r = await api.get('/push/vapid-public-key');
      const res = handleResponse(r);
      return res.success ? res.data?.publicKey || null : null;
    } catch { return null; }
  },

  /**
   * Native: prompt OS permission → register with APNs/FCM → POST the
   * device token to /api/push/native-subscribe. The token listener fires
   * asynchronously, so we resolve as soon as registration is requested;
   * the listener wires through CapacitorBootstrap.jsx.
   */
  async ensureNativeSubscribed() {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const perm = await PushNotifications.requestPermissions();
      if (perm.receive !== 'granted') {
        this.setDismissCooldown();
        return { success: false, reason: 'denied' };
      }
      // Register `registration` + `registrationError` listeners ONCE.
      // Idempotent — calling addListener twice is fine but we guard anyway.
      if (!this._nativeListenersAttached) {
        await PushNotifications.addListener('registration', async (token) => {
          try {
            await api.post('/push/native-subscribe', {
              token: token.value,
              platform: Capacitor.getPlatform(),  // 'ios' | 'android'
              appVersion: undefined,
              deviceName: navigator.userAgent,
            });
            this.clearCooldowns();
          } catch (e) { /* backend rejected; will retry on next launch */ }
        });
        await PushNotifications.addListener('registrationError', () => {
          this.setFailureCooldown();
        });
        this._nativeListenersAttached = true;
      }
      await PushNotifications.register();
      return { success: true };
    } catch (e) {
      this.setFailureCooldown();
      return { success: false, reason: 'subscribe_failed', error: e?.message };
    }
  },

  /**
   * Full subscribe flow: prompt permission (if not already decided),
   * fetch VAPID, subscribe with the service worker, POST to backend.
   * Returns {success, reason} where reason explains a refusal ('denied',
   * 'unsupported', 'no_vapid', etc.).
   */
  async ensureSubscribed() {
    if (this.isNative()) return await this.ensureNativeSubscribed();
    if (!this.isSupported()) return { success: false, reason: 'unsupported' };
    let perm = Notification.permission;
    if (perm === 'denied') {
      // User actively said no at the browser level — long cooldown.
      this.setDismissCooldown();
      return { success: false, reason: 'denied' };
    }
    if (perm === 'default') {
      try { perm = await Notification.requestPermission(); } catch {
        this.setDismissCooldown();
        return { success: false, reason: 'denied' };
      }
      if (perm !== 'granted') {
        this.setDismissCooldown();
        return { success: false, reason: 'denied' };
      }
    }

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const vapid = await this.getVapidKey();
      if (!vapid) {
        // Backend not configured yet — SHORT cooldown so we retry next session
        // once the operator drops the VAPID keys in. We don't want to long-cooldown
        // the user for a server-side misconfig they had no control over.
        this.setFailureCooldown();
        return { success: false, reason: 'no_vapid' };
      }
      try {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapid),
        });
      } catch (e) {
        // PushManager.subscribe can fail for transient reasons (network, push
        // service hiccup, key mismatch after a VAPID rotation). Treat as
        // recoverable — short cooldown, re-prompt next session.
        this.setFailureCooldown();
        return { success: false, reason: 'subscribe_failed', error: e?.message };
      }
    }

    // POST/refresh on the backend either way — handles user switching accounts
    // on the same browser (re-binds the subscription to the new user).
    const payload = sub.toJSON();
    try {
      await api.post('/push/subscribe', {
        endpoint: payload.endpoint,
        p256dh: payload.keys?.p256dh,
        auth: payload.keys?.auth,
        userAgent: navigator.userAgent,
      });
      // Success — drop any prior dismiss/failure cooldown so a future
      // 'permission revoked then re-granted' cycle isn't blocked.
      this.clearCooldowns();
      return { success: true };
    } catch (e) {
      // Backend POST blew up (network, auth, server error). Same as above —
      // short cooldown, retry next session.
      this.setFailureCooldown();
      return handleError(e);
    }
  },

  /**
   * Fire a server-side test push to every subscription registered for the
   * current user. Backend returns { delivered, subscriptions } — use to verify
   * end-to-end delivery from a UI button.
   */
  async sendTest() {
    try {
      const r = await api.post('/push/test');
      return handleResponse(r);
    } catch (e) { return handleError(e); }
  },

  /**
   * Tear down the local push subscription + tell the backend. Used by an
   * explicit user opt-out (settings page later). Branches native vs web.
   */
  async unsubscribe() {
    if (this.isNative()) {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        // No clean "drop this device's FCM token" API — the right call is
        // to remove the listener and let the backend prune the row when
        // the next send returns UNREGISTERED. But we can also be explicit:
        // there's no per-device token retrievable post-hoc; best we can do
        // is unregister all listeners.
        await PushNotifications.removeAllListeners();
        this._nativeListenersAttached = false;
        return { success: true };
      } catch (e) { return handleError(e); }
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try { await api.delete(`/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`); } catch {}
      }
      return { success: true };
    } catch (e) { return handleError(e); }
  },
};

export default pushService;
